import { useState } from "react"
import { useAuth } from "@/hooks/use-auth"
import { useToast } from "@/hooks/use-toast"
import { useQuery } from "@tanstack/react-query"
import { MobileNav } from "@/components/dashboard/coach/MobileNav"
import { SchoolSearch } from "@/components/schools/SchoolSearch"
import { SchoolCard } from "@/components/schools/SchoolCard"
import { GraduationCap, RefreshCw, ArrowLeft, Home, MapPin, Clock, Award, ExternalLink, X } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { createClient } from '@supabase/supabase-js'
import type { Database } from '@/types/supabase'
import { motion } from "framer-motion"
import { Link } from "react-router-dom"
import { ScrollArea } from "@/components/ui/scroll-area"

interface University {
  id: string
  name: string
  description: string
  location: string
  image_url: string | null
  certification: string
  duration: string
  website_url: string | null
  specialties: string[]
}

const MotionDiv = motion.div;

export function SleepUniversities() {
  return (
    <UniversitiesContent />
  )
}

const UniversitiesContent = () => {
  const [searchQuery, setSearchQuery] = useState("")
  const [selectedSchool, setSelectedSchool] = useState<University | null>(null)
  const { signOut } = useAuth()
  const { toast } = useToast()
  
  // Create a fresh Supabase client to ensure we bypass any caching
  const freshSupabase = createClient<Database>(
    import.meta.env.VITE_SUPABASE_URL,
    import.meta.env.VITE_SUPABASE_ANON_KEY,
    {
      auth: {
        autoRefreshToken: true,
        persistSession: false, // Don't persist session to avoid caching
      },
    }
  )

  const { data: universities = [], isLoading, refetch } = useQuery({
    queryKey: ['universities'],
    queryFn: async () => {
      // Add cache-busting timestamp to ensure fresh data
      console.log("Fetching universities data at:", new Date().toISOString());
      
      // Use fresh Supabase client to avoid any caching issues
      const { data: universitiesData, error: universitiesError } = await freshSupabase
        .from('sleep_universities')
        .select(`
          *,
          university_specialty_map(
            specialty_id,
            university_specialties(name)
          )
        `)
        .order('name', { ascending: true }) // Order by name to ensure consistent ordering

      if (universitiesError) {
        throw universitiesError
      }

      console.log("Raw universities data:", universitiesData);

      // Get unique universities by name
      const uniqueUniversities = universitiesData.reduce((acc: any[], uni: any) => {
        const exists = acc.find(u => u.name === uni.name)
        if (!exists) {
          acc.push(uni)
        }
        return acc
      }, [])

      const mappedUniversities = uniqueUniversities.map((uni: any): University => ({
        id: uni.id,
        name: uni.name,
        description: uni.description,
        location: uni.location,
        image_url: uni.image_url,
        certification: uni.certification,
        duration: uni.duration,
        website_url: uni.website_url,
        specialties: uni.university_specialty_map.map(
          (map: any) => map.university_specialties.name
        )
      }));

      console.log("Mapped universities:", mappedUniversities);
      
      // Auto-select first university if available
      if (mappedUniversities.length > 0 && !selectedSchool) {
        setSelectedSchool(mappedUniversities[0]);
      }
      
      return mappedUniversities;
    },
    staleTime: 0, // Consider data stale immediately
    gcTime: 0, // Remove from cache immediately when unused
    refetchOnMount: true, // Always refetch on component mount
    refetchOnWindowFocus: true, // Refetch when window regains focus
  })

  const handleSignOut = async () => {
    try {
      await signOut()
      toast({
        title: "Signed out successfully",
        description: "You have been signed out of your account",
      })
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Error signing out",
        description: "Please try again",
      })
    }
  }

  const filteredSchools = universities.filter(school =>
    school.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    school.location.toLowerCase().includes(searchQuery.toLowerCase()) ||
    school.specialties.some(specialty => 
      specialty.toLowerCase().includes(searchQuery.toLowerCase())
    )
  )

  return (
    <div className="min-h-screen bg-white dark:bg-slate-900 flex flex-col">
      {/* Header with navigation */}
      <header className="bg-gradient-to-br from-purple-800 to-indigo-900 text-white">
        <div className="container mx-auto px-4 py-4">
          <div className="flex justify-between items-center mb-4">
            <Link to="/" className="flex items-center gap-2 hover:text-purple-200 transition-colors">
              <ArrowLeft className="h-5 w-5" />
              <span>Back to Dashboard</span>
            </Link>
            
            <div className="flex gap-4">
              <Button 
                variant="outline" 
                size="sm" 
                className="bg-white/10 hover:bg-white/20 text-white border-white/20"
                onClick={() => {
                  refetch();
                  toast({
                    title: "Data refreshed",
                    description: "Latest university information has been loaded",
                  });
                }}
                disabled={isLoading}
              >
                <RefreshCw className={`mr-2 h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
              
              <Link to="/" className="inline-flex items-center justify-center px-4 py-2 bg-white/10 hover:bg-white/20 text-white rounded-md border border-white/20">
                <Home className="h-4 w-4 mr-2" />
                Home
              </Link>
            </div>
          </div>
          
          <div className="text-center mb-4">
            <div className="flex items-center justify-center space-x-3">
              <GraduationCap className="h-8 w-8 text-purple-200" />
              <h1 className="text-3xl font-bold tracking-tight text-white">
                Sleep Universities
              </h1>
            </div>
          </div>
        </div>
      </header>

      {/* Search Bar */}
      <div className="bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 shadow-sm">
        <div className="container mx-auto px-4 py-3">
          <SchoolSearch value={searchQuery} onChange={setSearchQuery} />
        </div>
      </div>

      {/* Main content - Grid View of Cards */}
      <div className="flex-1 bg-slate-50 dark:bg-slate-900/50 py-8">
        <div className="container mx-auto px-4">
          {isLoading ? (
            <div className="flex items-center justify-center w-full py-12">
              <div className="text-center">
                <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-purple-600 border-r-transparent"></div>
                <p className="mt-4 text-slate-700 dark:text-slate-300">Loading universities...</p>
              </div>
            </div>
          ) : (
            <>
              {filteredSchools.length === 0 ? (
                <div className="text-center py-16 max-w-md mx-auto">
                  <div className="rounded-full bg-slate-100 dark:bg-slate-800 p-4 inline-flex">
                    <GraduationCap className="h-8 w-8 text-slate-400" />
                  </div>
                  <h3 className="mt-4 text-lg font-medium text-slate-900 dark:text-white">No universities found</h3>
                  <p className="mt-2 text-slate-600 dark:text-slate-400">Try adjusting your search</p>
                  <Button 
                    className="mt-4"
                    variant="outline"
                    onClick={() => setSearchQuery("")}
                  >
                    View all programs
                  </Button>
                </div>
              ) : (
                <>
                  <div className="mb-6 flex justify-between items-center">
                    <h2 className="text-xl font-semibold text-slate-900 dark:text-white">
                      Available Programs ({filteredSchools.length})
                    </h2>
                    {searchQuery && (
                      <Button 
                        variant="ghost"
                        size="sm"
                        onClick={() => setSearchQuery("")}
                        className="text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200"
                      >
                        <X className="h-4 w-4 mr-2" />
                        Clear search
                      </Button>
                    )}
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                    {filteredSchools.map((school, index) => (
                      <MotionDiv
                        key={school.id}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ 
                          duration: 0.5, 
                          delay: index * 0.1,
                          ease: [0.22, 1, 0.36, 1]
                        }}
                        className="h-full"
                        whileHover={{ 
                          y: -8,
                          boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)" 
                        }}
                      >
                        <SchoolCard school={school} />
                      </MotionDiv>
                    ))}
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-slate-100 dark:bg-slate-800/50 py-4 border-t border-slate-200 dark:border-slate-700">
        <div className="container mx-auto px-4">
          <div className="flex justify-between items-center">
            <Link to="/" className="text-purple-600 dark:text-purple-400 hover:underline text-sm font-medium flex items-center gap-2">
              <Home className="h-3 w-3" />
              Return to SleepNest
            </Link>
            
            <div className="text-xs text-slate-500 dark:text-slate-400">
              SleepNest &copy; {new Date().getFullYear()}
            </div>
          </div>
        </div>
      </footer>
    </div>
  )
}
